import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ChevronDown, Shield, ArrowRight, CreditCard, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import NovaNavbar from '@/components/layout/NovaNavbar';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import { Language } from '@/types';

interface NovaPricingPageProps {
  lang: Language;
}

export type Market = 'EG' | 'SA' | 'Global';

// ─── SINGLE SOURCE OF TRUTH ───────────────────────────────────────────────────
export const VEXL_TIERS = (lang: Language, market: Market) => {
  const getPricing = (egPrice: number, egOriginal: number | null, saPrice: number, saOriginal: number | null, globalPrice: number, globalOriginal: number | null) => {
    switch(market) {
      case 'SA': return { price: saPrice, originalPrice: saOriginal, currency: lang === 'ar' ? 'ر.س' : 'SAR' };
      case 'Global': return { price: globalPrice, originalPrice: globalOriginal, currency: '$' };
      case 'EG': 
      default: return { price: egPrice, originalPrice: egOriginal, currency: lang === 'ar' ? 'ج.م' : 'EGP' };
    }
  };

  return [
    {
      id: 'starter',
      name: lang === 'ar' ? 'البداية المكانية' : 'SPATIAL STARTER',
      description: lang === 'ar' 
        ? 'سكربتات المعالجة المكانية الأساسية، استعلامات CRS، والعمليات التحليلية المعيارية'
        : 'Essential geoprocessing scripts, basic CRS lookups, and standard spatial queries',
      ...getPricing(0, null, 0, null, 0, null),
      period: null,
      cta: lang === 'ar' ? 'ابدأ مجاناً للمجتمع ←' : 'Start Free Community ←',
      badge: lang === 'ar' ? 'المجاني للمجتمع' : 'COMMUNITY FREE',
      launchTag: null,
      monthlyBreakdown: null,
      isFeatured: false,
      isEnterprise: false,
      points: lang === 'ar' ? [
        '15 استعلام استدلال مكاني يومياً',
        '5 سكربتات جيومعالجة معيارية / يوم',
        'التحقق المباشر من أنظمة الإسقاط (CRS/EPSG)',
        'استعلامات مكانية مجتمعية ونماذج مفتوحة',
        'خوادم الاستدلال المكاني المعيارية',
      ] : [
        '15 Spatial Reasoning Queries / Day',
        '5 Standard Geoprocessing Scripts / Day',
        'Direct CRS & Geodetic Datum Lookups',
        'Standard Spatial Queries & Community Models',
        'Standard Spatial Reasoning Engine Access',
      ],
    },
    {
      id: 'scholar',
      name: lang === 'ar' ? 'الباحث الأكاديمي' : 'RESEARCH SCHOLAR',
      description: lang === 'ar'
        ? 'حسابات الأطياف المتعددة، مؤشرات الاستشعار عن بعد (NDVI, LST)، وتوثيق المنهجيات'
        : 'Multi-spectral band arithmetic, Earth Observation indices (NDVI, NDWI, LST), and methodology export',
      ...getPricing(149, null, 49, null, 12.99, null),
      period: lang === 'ar' ? 'شهر' : 'month',
      cta: lang === 'ar' ? 'الاشتراك الأكاديمي ←' : 'Subscribe Scholar ←',
      badge: lang === 'ar' ? 'الباحث الأكاديمي والزمالة' : 'ACADEMIC & FELLOWSHIP',
      launchTag: null,
      monthlyBreakdown: null,
      isFeatured: false,
      isEnterprise: false,
      points: lang === 'ar' ? [
        '200 استعلام استدلال مكاني يومياً',
        '50 سكربت جيومعالجة متقدم / يوم',
        'حسابات الأطياف المتعددة (NDVI, NDWI, LST, NBR)',
        'تصدير المنهجيات البحثية المعتمدة بصيغة Word / DOCX',
        'خزنة المشاريع الأكاديمية وحفظ الأصول العلمية',
      ] : [
        '200 Spatial Reasoning Queries / Day',
        '50 Advanced Geoprocessing Scripts / Day',
        'Multi-Spectral Band Arithmetic (NDVI, NDWI, LST)',
        'Citation-Ready Academic Methodology Export (DOCX)',
        'Research Project Vault & Saved Artifacts',
      ],
    },
    {
      id: 'engineer',
      name: lang === 'ar' ? 'المهندس المكاني' : 'SPATIAL ENGINEER',
      description: lang === 'ar'
        ? 'أدوات ArcPy .pyt، تحسين استعلامات PostGIS، معالج التبولوجيا، وخطوط إنتاج PyQGIS'
        : 'ArcPy .pyt toolboxes, PostGIS optimization, topology solver, and batch PyQGIS pipelines',
      ...getPricing(299, null, 99, null, 24.99, null),
      period: lang === 'ar' ? 'شهر' : 'month',
      cta: lang === 'ar' ? 'ترقية المهندس المكاني ←' : 'Deploy Engineer Tier ←',
      badge: lang === 'ar' ? 'المهندس المكاني المحترف' : 'SPATIAL ENGINEER // PRO',
      launchTag: null,
      monthlyBreakdown: null,
      isFeatured: true,
      isEnterprise: false,
      points: lang === 'ar' ? [
        'استعلامات وأكواد غير محدودة وبأعلى أولوية معالجة',
        'صناديق أدوات ArcPy (.pyt) وخطوط إنتاج PyQGIS الدفعية',
        'تحسين استعلامات PostGIS المعقدة والفهرسة (H3, S2)',
        'معالج التبولوجيا المستقل وتصحيح انزلاقات البولجون',
        'الخريطة التفاعلية الحية Cartograph ومحرك DuckDB',
      ] : [
        'Unlimited Reasoning Queries & Code Generation',
        'Production ArcPy .pyt Toolboxes & Batch PyQGIS',
        'PostGIS Query Optimization & Spatial Indexing',
        'Autonomous GIS Topology Solver & Sliver Geometry Healer',
        'Live Spatial Map Sandbox (Cartograph) & DuckDB',
      ],
    },
    {
      id: 'enterprise',
      name: lang === 'ar' ? 'المؤسسات السيادية' : 'ENTERPRISE SOVEREIGN',
      description: lang === 'ar'
        ? 'تشغيل محلي معزول (Air-Gapped)، معالجة بيانات الأقمار الخاصة، واتفاقيات مستوى خدمة مخصصة'
        : 'On-premise air-gapped deployment, private satellite constellation pipelines, and custom SLAs',
      ...getPricing(0, null, 0, null, 0, null),
      period: lang === 'ar' ? 'سنوي / مخصص' : 'annual / custom',
      cta: lang === 'ar' ? 'طلب النشر السيادي ←' : 'Contact Sovereign Team ←',
      badge: lang === 'ar' ? 'المؤسسات والمختبرات السيادية' : 'ENTERPRISE SOVEREIGN',
      launchTag: null,
      monthlyBreakdown: null,
      isFeatured: false,
      isEnterprise: true,
      points: lang === 'ar' ? [
        'نشر معزول محلياً داخل المنشأة (On-Premise Air-Gapped)',
        'معالجة خطوط بيانات كوكبات الأقمار الصناعية الخاصة',
        'نماذج استدلال جيوديسي مخصصة وخوادم ArcPy Server خاصة',
        'اتفاقية مستوى خدمة مخصصة (SLA 99.99%) مع مهندسين معتمدين',
        'إدارة صلاحيات مؤسسية متعددة المقاعد وتشفير سيادي متكامل',
      ] : [
        'On-Premise Air-Gapped Deployment (Zero-Egress Security)',
        'Private Satellite Constellation Telemetry Pipelines',
        'Custom Fine-Tuned Geodetic Models & Dedicated ArcPy Runners',
        'Enterprise SLA (99.99%) & Dedicated Spatial Safety Engineers',
        'Multi-Seat Sovereign Access & Centralized Governance',
      ],
    },
  ];
};

// ─── Feature comparison table config ─────────────────────────────────────────
const getFeatureTable = (lang: Language) => [
  { 
    feature: lang === 'ar' ? 'الرسائل والاستعلامات المكانية' : 'Daily Reasoning Messages', 
    starter: '15 / day', 
    scholar: '200 / day', 
    engineer: 'Unlimited', 
    enterprise: 'Unlimited / Dedicated' 
  },
  { 
    feature: lang === 'ar' ? 'توليد وتصحيح أكواد Geoprocessing' : 'Geoprocessing Code Scripts', 
    starter: '5 / day', 
    scholar: '50 / day', 
    engineer: 'Unlimited', 
    enterprise: 'Unlimited / Custom' 
  },
  { 
    feature: lang === 'ar' ? 'حسابات الأطياف ومؤشرات الاستشعار (NDVI, LST)' : 'Multi-Spectral & EO Indices (NDVI, LST)', 
    starter: false, 
    scholar: true, 
    engineer: true, 
    enterprise: true 
  },
  { 
    feature: lang === 'ar' ? 'كاتب المنهجيات والتوثيق الأكاديمي (DOCX)' : 'Academic Methodology Writer (DOCX)', 
    starter: false, 
    scholar: true, 
    engineer: true, 
    enterprise: true 
  },
  { 
    feature: lang === 'ar' ? 'أدوات ArcPy .pyt وحزم PyQGIS للإنتاج' : 'ArcPy .pyt Toolboxes & PyQGIS Pipelines', 
    starter: false, 
    scholar: false, 
    engineer: true, 
    enterprise: true 
  },
  { 
    feature: lang === 'ar' ? 'الخريطة التفاعلية ومحرك DuckDB مكاني' : 'Cartograph Sandbox & Spatial DuckDB', 
    starter: false, 
    scholar: false, 
    engineer: true, 
    enterprise: true 
  },
  { 
    feature: lang === 'ar' ? 'معالج التبولوجيا وحل تشوهات الأشكال الهندسية' : 'Autonomous Topology & Geometry Healer', 
    starter: false, 
    scholar: false, 
    engineer: true, 
    enterprise: true 
  },
  { 
    feature: lang === 'ar' ? 'خزنة المشاريع ومستودع الأكواد (Workspace Vault)' : 'Spatial Workspace Vault & Artifacts', 
    starter: false, 
    scholar: true, 
    engineer: 'Unlimited', 
    enterprise: 'Unlimited / Air-Gapped' 
  },
  { 
    feature: lang === 'ar' ? 'التشغيل المحلي المعزول (Air-Gapped On-Premise)' : 'Air-Gapped On-Premise Deployment', 
    starter: false, 
    scholar: false, 
    engineer: false, 
    enterprise: true 
  },
  { 
    feature: lang === 'ar' ? 'اتفاقيات مستوى الخدمة (SLA) وحماية سيادية' : 'Enterprise SLA & Sovereign Protection', 
    starter: false, 
    scholar: false, 
    engineer: false, 
    enterprise: true 
  },
];

// ─── FAQ config ───────────────────────────────────────────────────────────────
const getFaqs = (lang: Language) => [
  {
    q: lang === 'ar' ? 'كيف يعمل تسعير VEXL Nova؟' : 'How does VEXL Nova’s pricing work?',
    a: lang === 'ar'
      ? 'يقدم VEXL Nova خططاً مرنة مصممة للمهندسين المستقلين، والباحثين، والمؤسسات. يمكنك البدء بالخطة المجانية، أو الترقية إلى Nova Pro للوصول غير المحدود لمحرك الاستدلال المكاني، أو التواصل معنا للحصول على حلول مخصصة للمؤسسات.'
      : 'VEXL Nova offers flexible plans designed for individual spatial researchers, engineers, and enterprise organizations. You can start with our Free tier, upgrade to Pro for unlimited spatial reasoning, or contact us for customized institutional deployments.',
  },
  {
    q: lang === 'ar' ? 'هل VEXL Nova مجاني للاستخدام؟' : 'Is VEXL Nova free to use?',
    a: lang === 'ar'
      ? 'نعم. تتيح الخطة المجانية لأي مستخدم تجربة محرك التفكير المكاني الأساسي وتوليد أكواد ArcPy و QGIS مع حصة استخدام يومية مجانية.'
      : 'Yes. Our Free tier allows anyone to test Nova’s core spatial reasoning, basic ArcPy/QGIS script generation, and community models with daily usage quotas.',
  },
  {
    q: lang === 'ar' ? 'هل تقدم VEXL خططاً للجامعات والمؤسسات التعليمية؟' : 'Does VEXL offer plans for universities and educational institutions?',
    a: lang === 'ar'
      ? 'نعم. نقدم تراخيص أكاديمية مدعومة وحزم حوسبة مخصصة للجامعات، والمختبرات البحثية، وأقسام نظم المعلومات الجغرافية. تواصل مع فريقنا لترتيب وصول مؤسسي.'
      : 'Yes. We offer subsidized academic campus licenses and dedicated compute clusters for universities, research labs, and GIS departments. Contact our team for institutional access.',
  },
  {
    q: lang === 'ar' ? 'هل تقدم VEXL خصومات لفرق الأبحاث والمؤسسات غير الربحية؟' : 'Does VEXL offer discounts for research teams and nonprofits?',
    a: lang === 'ar'
      ? 'نعم. نوفر منحاً مخصصة وتخفيضات لمبادرات حماية البيئة غير الربحية، وفرق رسم الخرائط الإنسانية، وبرامج الإقامة البحثية الأكاديمية.'
      : 'We provide specialized grants and discounted tiers for verified non-profit environmental initiatives, humanitarian mapping teams, and academic research residency programs.',
  },
  {
    q: lang === 'ar' ? 'كم عدد المستخدمين المدعومين في كل خطة اشتراك؟' : 'How many users are supported on each subscription plan?',
    a: lang === 'ar'
      ? 'الخطط الفردية (Free و Pro) مخصصة لمستخدم واحد لكل حساب. بالنسبة للفرق والمختبرات والمؤسسات التي تحتاج موارد حوسبة مشتركة ولوحة تحكم مركزية، تتوفر تراخيص متعددة المقاعد.'
      : 'Free and Pro plans are licensed on a per-user basis. For teams, labs, and enterprise departments requiring pooled spatial compute and centralized admin controls, multi-seat licensing is available.',
  },
  {
    q: lang === 'ar' ? 'ما هي خيارات وطرق الدفع المتاحة؟' : 'What are my payment options?',
    a: lang === 'ar'
      ? 'نقبل جميع البطاقات الائتمانية الدولية (Visa و Mastercard). وبالنسبة للمستخدمين في مصر والشرق الأوسط، ندعم بوابات الدفع والتحويل المحلي مثل InstaPay و Vodafone Cash.'
      : 'We accept major international credit and debit cards (Visa, Mastercard, American Express). For regional users, local payment gateways including InstaPay and Vodafone Cash are also supported.',
  },
  {
    q: lang === 'ar' ? 'ما مدى أمان VEXL Nova وحماية البيانات المكانية الخاصة؟' : 'How secure is VEXL Nova with proprietary spatial data?',
    a: lang === 'ar'
      ? 'تتم كافة عمليات معالجة البيانات المكانية داخل بيئات معزولة (Sandboxes) مع تشفير كامل للبيانات أثناء النقل (TLS 1.3) والتخزين (AES-256). كما نوفر خيارات تشغيل محلي (On-Premise) للمؤسسات.'
      : 'All spatial data processing occurs within isolated compute sandboxes with end-to-end TLS 1.3 encryption in transit and AES-256 at rest. Enterprise deployments can also operate in zero-retention or private on-premise environments.',
  },
  {
    q: lang === 'ar' ? 'كيف تتعامل VEXL مع بياناتي وأكواد المعالجة المكانية؟' : 'How does VEXL use my code and spatial data?',
    a: lang === 'ar'
      ? 'نحن لا نستخدم بياناتك المكانية الخاصة أو طبقات الـ Vector أو أكواد بايثون لتدريب نماذج الذكاء الاصطناعي العامة. تظل بياناتك ومشاريعك ملكيتك الفكرية الكاملة.'
      : 'We do not train our foundational models on your private spatial datasets, vector layers, or proprietary Python scripts. Your data remains strictly your intellectual property.',
  },
];

// ─── FAQ Component ────────────────────────────────────────────────────────────
const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left group"
      >
        <span className="text-base font-semibold text-slate-200 group-hover:text-[#9f96f3] transition-colors pr-8">
          {question}
        </span>
        <ChevronDown
          size={18}
          className={`text-slate-500 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#9f96f3]' : ''}`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-slate-400 leading-relaxed text-[15px]">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Cell Renderer ────────────────────────────────────────────────────────────
const FeatureCell: React.FC<{ value: boolean | string }> = ({ value }) => {
  if (value === true) return <Check size={18} className="text-[#9f96f3] mx-auto" />;
  if (value === false) return <X size={18} className="text-white/15 mx-auto" />;
  return <span className="text-xs font-bold text-slate-300">{value}</span>;
};

// ─── Market Selector Component ────────────────────────────────────────────────
const MarketSelector: React.FC<{ market: Market, setMarket: (m: Market) => void, lang: Language }> = ({ market, setMarket, lang }) => {
  const markets: { id: Market, labelAr: string, labelEn: string }[] = [
    { id: 'EG', labelAr: 'مصر', labelEn: 'Egypt' },
    { id: 'SA', labelAr: 'السعودية', labelEn: 'Saudi Arabia' },
    { id: 'Global', labelAr: 'الدولي', labelEn: 'Global' },
  ];

  return (
    <div className="flex items-center justify-center mb-12">
      <div className="bg-white/5 border border-white/10 rounded-full p-1 flex gap-1">
        {markets.map((m) => {
          const isActive = market === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setMarket(m.id)}
              className={`
                relative px-5 py-2 rounded-full text-xs font-semibold transition-all duration-300 flex items-center justify-center
                ${isActive ? 'text-white' : 'text-slate-400 hover:text-slate-200'}
              `}
            >
              {isActive && (
                <motion.div
                  layoutId="market-selector"
                  className="absolute inset-0 bg-[#7F77DD]/20 border border-[#7F77DD]/50 rounded-full"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-10">{lang === 'ar' ? m.labelAr : m.labelEn}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const NovaPricingPage: React.FC<NovaPricingPageProps> = ({ lang }) => {
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [market, setMarket] = useState<Market>('EG');

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data?.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  const handleCTA = (tier: any) => {
    if (tier.id === 'starter' || (tier.price === 0 && !tier.isEnterprise)) {
      if (session) {
        navigate('/nova-workspace');
      } else {
        navigate('/nova-workspace?auth=true');
      }
    } else if (tier.isEnterprise || tier.id === 'enterprise') {
      const enterpriseMsg = lang === 'ar'
        ? 'مرحباً%20بفريق%20VEXL،%20نود%20الاستفسار%20عن%20باقة%20Enterprise%20Sovereign%20للنشر%20المؤسسي%20المعزول%20محلياً'
        : 'Hello%20VEXL%20Team,%20we%20are%20inquiring%20about%20the%20Enterprise%20Sovereign%20air-gapped%20deployment';
      window.open(`https://wa.me/201279298987?text=${enterpriseMsg}`, '_blank', 'noopener,noreferrer');
    } else {
      let planName = 'Research%20Scholar';
      if (tier.id === 'engineer') planName = 'Spatial%20Engineer';

      let message = `مرحباً%20بفريق%20VEXL،%20أريد%20الاشتراك%20في%20خطة%20${planName}`;
      if (market === 'SA' || market === 'Global') {
        message = `مرحباً%20بفريق%20VEXL،%20أريد%20الاشتراك%20في%20خطة%20${planName}%20باستخدام%20الفيزا/ماستركارد`;
      }

      window.open(`https://wa.me/201279298987?text=${message}`, '_blank', 'noopener,noreferrer');
    }
  };

  const activeTiers = VEXL_TIERS(lang, market);
  const featureTable = getFeatureTable(lang);
  const faqs = getFaqs(lang);

  return (
    <div className="bg-[#02040a] min-h-screen text-white selection:bg-[#7F77DD]/35 selection:text-white">

      <PageTransition>
        <SEO
          title="VEXL Nova Pricing | Sovereign Spatial Intelligence"
          description={lang === 'ar' ? 'خطط أسعار VEXL Nova للمهندسين المكانيين والباحثين الأكاديميين والمؤسسات السيادية في GIS والاستشعار عن بعد.' : 'VEXL Nova pricing plans for spatial engineers, academic researchers, and sovereign institutions in GIS and remote sensing.'}
          lang={lang}
        />

        <div className="min-h-screen pb-32 overflow-x-hidden relative">
          <div className="relative z-10 pt-24 md:pt-28 px-6">
            <div className="max-w-6xl mx-auto">

              {/* ── Header ──────────────────────────────── */}
              <div className="text-center max-w-2xl mx-auto mb-12">
                <motion.h1
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4"
                >
                  {lang === 'ar' ? 'الأسعار والتراخيص' : 'Pricing & Licensing'}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="text-slate-400 text-base md:text-lg font-normal leading-relaxed"
                >
                  {lang === 'ar'
                    ? 'خطط استدلال مكاني مصممة للباحثين الأكاديميين، مهندسي نظم المعلومات، والمؤسسات السيادية.'
                    : 'Spatial reasoning tiers architected for academic researchers, GIS engineers, and sovereign institutions.'}
                </motion.p>
              </div>

              {/* ── Market Selector ─────────────────────── */}
              <MarketSelector market={market} setMarket={setMarket} lang={lang} />

              {/* ── Pricing Cards ───────────────────────── */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-28">
                {activeTiers.map((tier, i) => {
                  const isFeatured = tier.isFeatured;
                  return (
                    <motion.div
                      key={tier.id}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                      className={`
                        relative flex flex-col rounded-2xl p-8 border transition-all duration-300
                        hover:-translate-y-1.5
                        ${isFeatured
                          ? 'bg-[#060812] border-[#7F77DD]/50 shadow-[0_0_30px_rgba(127,119,221,0.15)]'
                          : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                        }
                      `}
                    >
                      {/* Badge */}
                      {tier.badge && (
                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-[0.2em] px-3.5 py-1 rounded-full whitespace-nowrap ${isFeatured ? 'bg-[#7F77DD] text-white' : 'bg-white/10 text-slate-300 border border-white/15'}`}>
                          {tier.badge}
                        </div>
                      )}

                      {/* Plan Title & Value Proposition */}
                      <div className="mb-6">
                        <h2 className="text-2xl font-bold text-white tracking-tight mb-2">
                          {tier.name}
                        </h2>
                        <p className="text-slate-400 text-xs leading-relaxed min-h-[34px]">
                          {tier.description}
                        </p>
                      </div>

                      {/* Price Section */}
                      <div className="mb-8 pb-6 border-b border-white/10">
                        {tier.isEnterprise ? (
                          <div className="flex flex-col items-start">
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-3xl font-bold text-white tracking-tight">
                                {lang === 'ar' ? 'مخصص' : 'Custom'}
                              </span>
                            </div>
                            <span className="text-slate-400 text-xs font-normal mt-1">
                              {lang === 'ar' ? 'نشر محلي معزول / ترخيص سنوي' : 'On-Premise Air-Gapped / Custom SLA'}
                            </span>
                          </div>
                        ) : tier.price === 0 ? (
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-4xl font-bold text-white tracking-tight">
                              {tier.currency === '$' ? '$0' : (lang === 'ar' ? '0 ج.م' : '0 EGP')}
                            </span>
                            <span className="text-slate-400 text-xs font-normal">
                              / {lang === 'ar' ? 'مجاناً دائماً' : 'forever free'}
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-start">
                            {tier.originalPrice && (
                              <span className="text-xs font-semibold text-slate-500 line-through mb-1">
                                {tier.currency === '$' ? `$${tier.originalPrice}` : `${tier.originalPrice} ${tier.currency}`}
                              </span>
                            )}
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-4xl font-bold text-white tracking-tight">
                                {tier.currency === '$' ? `$${tier.price}` : tier.price}
                              </span>
                              <span className="text-slate-400 text-xs font-normal">
                                {tier.currency !== '$' ? tier.currency : ''} / {tier.period}
                              </span>
                            </div>
                            {tier.monthlyBreakdown && (
                              <span className="text-[10px] font-semibold text-[#b7aff8] mt-2 bg-[#7F77DD]/15 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                {tier.monthlyBreakdown}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Feature Points */}
                      <ul className="flex flex-col gap-3 mb-10 flex-grow">
                        {tier.points.map((point) => (
                          <li key={point} className="flex items-start gap-3 text-sm">
                            <Check size={14} className={`mt-0.5 shrink-0 ${isFeatured ? 'text-[#9f96f3]' : 'text-slate-400'}`} />
                            <span className="text-slate-300 leading-tight">{point}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <button
                        onClick={() => handleCTA(tier)}
                        className={`
                          group w-full py-3.5 rounded-full font-bold text-xs tracking-wide
                          flex items-center justify-center gap-2 transition-all duration-300
                          ${isFeatured
                            ? 'bg-[#7F77DD] text-white hover:bg-[#928be6]'
                            : 'bg-white/5 text-slate-200 hover:bg-white/10 border border-white/10'
                          }
                        `}
                      >
                        {tier.cta}
                        <ArrowRight size={14} className={`group-hover:translate-x-1 transition-transform ${lang === 'ar' ? 'rotate-180' : ''}`} />
                      </button>
                    </motion.div>
                  );
                })}
              </div>

              {/* ── Feature Comparison Table ─────────────── */}
              <div className="mb-28">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-3">
                    {lang === 'ar' ? 'مصفوفة مقارنة القدرات الهندسية' : 'Deep Dive Engineering Matrix'}
                  </h2>
                  <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-slate-500">
                    {lang === 'ar' ? 'مقارنة شاملة لجميع التراخيص' : 'Full Capability Specification'}
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="py-5 px-4 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 w-1/4">
                          {lang === 'ar' ? 'القدرة الهندسية' : 'Engineering Capability'}
                        </th>
                        <th className="py-5 px-4 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {lang === 'ar' ? 'البداية المكانية' : 'Spatial Starter'}
                        </th>
                        <th className="py-5 px-4 text-center text-[10px] font-bold uppercase tracking-widest text-slate-300">
                          {lang === 'ar' ? 'الباحث الأكاديمي' : 'Research Scholar'}
                        </th>
                        <th className="py-5 px-4 text-center text-[10px] font-bold uppercase tracking-widest text-[#9f96f3]">
                          {lang === 'ar' ? 'المهندس المكاني' : 'Spatial Engineer'}
                        </th>
                        <th className="py-5 px-4 text-center text-[10px] font-bold uppercase tracking-widest text-slate-300">
                          {lang === 'ar' ? 'المؤسسات السيادية' : 'Enterprise Sovereign'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {featureTable.map((row, i) => (
                        <motion.tr
                          key={i}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.04 }}
                          className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                        >
                          <td className="py-5 px-4 text-sm font-semibold text-slate-300">{row.feature}</td>
                          <td className="py-5 px-4 text-center"><FeatureCell value={row.starter} /></td>
                          <td className="py-5 px-4 text-center"><FeatureCell value={row.scholar} /></td>
                          <td className="py-5 px-4 text-center"><FeatureCell value={row.engineer} /></td>
                          <td className="py-5 px-4 text-center"><FeatureCell value={row.enterprise} /></td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── FAQ Section ─────────────────────────── */}
              <div className="max-w-3xl mx-auto mb-28">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-8 h-px bg-white/10" />
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-white">
                    {lang === 'ar' ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
                  </h2>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                <div className="bg-white/[0.02] border border-white/10 rounded-2xl px-8 py-4">
                  {faqs.map((faq, i) => (
                     <FAQItem key={i} question={faq.q} answer={faq.a} />
                  ))}
                </div>
              </div>

              {/* ── Bottom Trust Bar ─────────────────────── */}
              <div className="text-center border-t border-white/5 pt-16 pb-8">
                <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 mb-8">
                  {[
                    { icon: <CreditCard size={14} />, label: market === 'EG' ? 'Vodafone Cash & InstaPay' : 'Visa & Mastercard' },
                    { icon: <Shield size={14} />, label: 'Cancel Anytime' },
                    { icon: <MessageSquare size={14} />, label: 'WhatsApp Support' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                      {item.icon}
                      {item.label}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-600">
                  © {new Date().getFullYear()} VEXL GeoAI Platform. All Rights Reserved.
                </p>
              </div>

            </div>
          </div>
        </div>
      </PageTransition>
    </div>
  );
};

export default NovaPricingPage;
